import { useEffect, useRef, useState } from "react";
import { authClient } from "@/lib/auth-client";

interface AdPlacementProps {
  slot?: string;
  className?: string;
}

// Singleton script loader — prevents duplicate <script> injection across multiple AdPlacement instances.
type ScriptState = "idle" | "loading" | "loaded" | "failed";
let adScriptState: ScriptState = "idle";
const adScriptCallbacks: ((success: boolean) => void)[] = [];

function loadAdSenseScript(
  publisherId: string,
  onResult: (success: boolean) => void,
): void {
  if (adScriptState === "loaded") {
    onResult(true);
    return;
  }
  if (adScriptState === "failed") {
    onResult(false);
    return;
  }
  adScriptCallbacks.push(onResult);
  if (adScriptState === "loading") return;

  adScriptState = "loading";

  // The loader may already be present in <head> (added in __root for the AdSense
  // verification crawler). Reuse it instead of injecting a duplicate — pushing
  // to window.adsbygoogle queues until the async script finishes loading.
  const existing = document.querySelector(
    'script[src^="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js"]',
  );
  if (existing) {
    adScriptState = "loaded";
    for (const cb of adScriptCallbacks.splice(0)) {
      try {
        cb(true);
      } catch {}
    }
    return;
  }

  const script = document.createElement("script");
  script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${publisherId}`;
  script.async = true;
  script.crossOrigin = "anonymous";
  script.onload = () => {
    adScriptState = "loaded";
    for (const cb of adScriptCallbacks.splice(0)) {
      try {
        cb(true);
      } catch {}
    }
  };
  script.onerror = () => {
    adScriptState = "failed";
    for (const cb of adScriptCallbacks.splice(0)) {
      try {
        cb(false);
      } catch {}
    }
  };
  document.head.appendChild(script);
}

/**
 * Renders a responsive Google AdSense display unit.
 * Renders nothing when the user is a paid subscriber or AdSense is not configured.
 *
 * AdSense is loaded LAZILY via IntersectionObserver — the adsbygoogle.js script
 * is only injected when this container is near the viewport.
 *
 * The container starts with zero height. A MutationObserver watches for AdSense
 * to inject an iframe (proof an ad filled). Only then does the container expand.
 * This avoids empty 250px gaps when ads aren't approved or are blocked.
 * Since ads load 300px before the viewport (IntersectionObserver rootMargin),
 * the expansion happens off-screen — no CLS impact.
 */
export function AdPlacement({ slot, className }: AdPlacementProps) {
  const { data: session } = authClient.useSession();
  const isPaidUser =
    (session?.user as { isPro?: boolean } | undefined)?.isPro ?? false;
  const containerRef = useRef<HTMLDivElement>(null);
  const pushed = useRef(false);
  const [mounted, setMounted] = useState(false);
  // "pending" until AdSense reports a result. AdSense injects an iframe even
  // when it has NO ad to show, so iframe presence is NOT proof an ad filled —
  // we must read its authoritative data-ad-status ("filled" | "unfilled").
  const [adStatus, setAdStatus] = useState<"pending" | "filled" | "unfilled">(
    "pending",
  );

  const publisherId = import.meta.env.VITE_ADSENSE_PUBLISHER_ID;
  const displaySlot = slot ?? import.meta.env.VITE_ADSENSE_DISPLAY_SLOT;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (
      !mounted ||
      isPaidUser ||
      !publisherId ||
      !displaySlot ||
      pushed.current
    )
      return;

    const container = containerRef.current;
    if (!container) return;

    const pushAd = (scriptLoaded: boolean) => {
      if (!scriptLoaded || pushed.current) return;
      pushed.current = true;
      try {
        // @ts-expect-error adsbygoogle is injected by the AdSense script
        window.adsbygoogle = window.adsbygoogle || [];
        // @ts-expect-error adsbygoogle is injected by the AdSense script
        window.adsbygoogle.push({});
      } catch {
        // AdSense not loaded (dev, ad-blocked) — fail silently
      }
    };

    // Load AdSense only when this container is within 300px of the viewport.
    // This keeps the 1800ms+ AdSense script execution out of the TBT window.
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.disconnect();
          loadAdSenseScript(publisherId, pushAd);
        }
      },
      { rootMargin: "300px" },
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [mounted, isPaidUser, publisherId, displaySlot]);

  // Watch the <ins> data-ad-status — AdSense sets it to "filled" or "unfilled"
  // after it tries to serve. "unfilled" ads still get an injected iframe (often
  // ~250-400px tall), so without this the container would reserve blank space.
  useEffect(() => {
    if (!mounted || adStatus !== "pending") return;

    const container = containerRef.current;
    if (!container) return;

    const read = (): boolean => {
      const ins = container.querySelector("ins.adsbygoogle");
      const status = ins?.getAttribute("data-ad-status");
      if (status === "filled") {
        setAdStatus("filled");
        return true;
      }
      if (status === "unfilled") {
        setAdStatus("unfilled");
        return true;
      }
      return false;
    };

    if (read()) return;

    const mutationObserver = new MutationObserver(() => {
      if (read()) mutationObserver.disconnect();
    });

    mutationObserver.observe(container, {
      attributes: true,
      subtree: true,
      attributeFilter: ["data-ad-status"],
    });
    return () => mutationObserver.disconnect();
  }, [mounted, adStatus]);

  // If ads not configured or user is paid, render nothing (no space reserved)
  if (!publisherId || !displaySlot || isPaidUser) return null;

  // The slot stays fully collapsed (0 height, no margin) UNTIL AdSense reports a
  // real ad ("filled"). It expands only then. During "pending" and "unfilled" it
  // is height-capped to 0 with overflow hidden, so the blank iframe AdSense
  // injects for an unfilled slot is clipped and never shows.
  // Why this is safe: the <ins> is a responsive unit (data-ad-format="auto"),
  // which AdSense sizes from the container's WIDTH (always full-width here) — a
  // 0 visible height does not stop it from filling. This avoids both the large
  // empty gap AND the load-then-collapse layout shift (CLS) of capping only
  // after "unfilled".
  // The <ins> tag is only rendered client-side (after mount) to avoid
  // SSR/hydration mismatch error #418 from AdSense DOM mutation.
  const filled = adStatus === "filled";
  return (
    <div
      ref={containerRef}
      className={filled ? className : undefined}
      style={{
        minHeight: filled ? "250px" : 0,
        maxHeight: filled ? undefined : 0,
        overflow: "hidden",
        maxWidth: "100%",
      }}
    >
      {mounted && (
        <ins
          className="adsbygoogle"
          style={{ display: "block", maxWidth: "100%" }}
          data-ad-client={publisherId}
          data-ad-slot={displaySlot}
          data-ad-format="auto"
          data-full-width-responsive="true"
        />
      )}
    </div>
  );
}
